const DEFAULT_MAX_DIMENSION = 1920;
const DEFAULT_QUALITY = 0.82;

export interface CompressImageOptions {
  /** Longest edge in px; larger images are downscaled, smaller ones untouched. */
  maxDimension?: number;
  /** WebP encoder quality (0-1). */
  quality?: number;
}

/**
 * Downscale and re-encode an image to WebP in the browser before upload,
 * so multi-hundred-KB originals never reach storage.
 *
 * Falls back to the original file when the browser cannot decode it
 * (e.g. HEIC without a decoder), when encoding is unavailable, or when
 * the result would not actually be smaller. GIF and SVG are passed
 * through untouched (animation / vector would be destroyed by canvas).
 */
export async function compressImageToWebP(
  file: File,
  options: CompressImageOptions = {}
): Promise<File> {
  const { maxDimension = DEFAULT_MAX_DIMENSION, quality = DEFAULT_QUALITY } =
    options;

  if (!file.type.startsWith('image/')) return file;
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(
      1,
      maxDimension / Math.max(bitmap.width, bitmap.height)
    );
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/webp', quality)
    );
    // Some browsers ignore the requested type and return PNG — treat as unsupported.
    if (!blob || blob.type !== 'image/webp') return file;
    // Keep the original when conversion didn't shrink it and no downscale happened.
    if (blob.size >= file.size && scale === 1) return file;

    const name = file.name.replace(/\.[^.]+$/, '') + '.webp';
    return new File([blob], name, { type: 'image/webp' });
  } catch {
    return file;
  }
}

/**
 * Compress every image File entry in a FormData in place.
 * Keys in `skipKeys` (e.g. favicon) keep their original file.
 */
export async function compressFormDataImages(
  formData: FormData,
  skipKeys: string[] = [],
  options: CompressImageOptions = {}
): Promise<FormData> {
  const entries: Array<[string, FormDataEntryValue]> = [];
  formData.forEach((value, key) => entries.push([key, value]));

  for (const [key, value] of entries) {
    if (value instanceof File && !skipKeys.includes(key)) {
      const compressed = await compressImageToWebP(value, options);
      if (compressed !== value) formData.set(key, compressed);
    }
  }
  return formData;
}
